import { useCallback } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';

import { useDebouncedSave } from 'hooks/useDebounceSave';

import { IObjectWithId } from 'interfaces/common';

import { getTotalItems } from 'components/_basic/Form/FieldTable/components/FieldTableTotalRow/helpers';
import { FORM_TABLE_COLUMN_NAME } from 'components/_basic/Form/FormTableComponent/constants';
import { LOOK, TotalSumInput } from 'components/_basic/TotalSumInput';
import { ILook } from 'components/_basic/TotalSumInput/types';

import { getTotalItemsWeightedAverage } from './helper';
import { IFormTableTotalProps } from './types';
import { EXPENSE_COLUMN_NAMES } from '../../utils/form';

import styles from './FormTableTotal.module.scss';

export const ExpensesFormTotalRow: React.FC<IFormTableTotalProps> = ({
  name,
  firstColumnWidth,
  expectedTotals,
  disabled,
  onErrorStateChange,
  onTotalCalculate,
  onTotalPercentCalculate,
}) => {
  const { t } = useTranslation();

  const rows: Record<string, string>[] = useWatch({ name });
  const totalRow = getTotalItems(rows);
  const weightedAveragePercent = getTotalItemsWeightedAverage(rows);

  const getClassForColumn = useCallback(
    (columnIndex: number): ILook => {
      if (columnIndex === 1) {
        return LOOK.SUCCESS;
      }
      const total = totalRow[`${FORM_TABLE_COLUMN_NAME}${columnIndex}`];
      const expected = Number(expectedTotals[columnIndex]);
      return total === 0 || total === expected ? LOOK.SUCCESS : LOOK.FAILED;
    },
    [expectedTotals, totalRow],
  );

  const updateError = () => {
    const foundError = Object.keys(expectedTotals).some(
      (columnIndex) => getClassForColumn(Number(columnIndex)) === LOOK.FAILED,
    );

    if (onErrorStateChange) {
      onErrorStateChange(foundError);
    }
  };

  const handleDebouncedSave = (value: IObjectWithId) => {
    updateError();
    if (onTotalCalculate) {
      onTotalCalculate(value);
    }
  };

  const handleWeightedAveragePercentSave = (value: string) => {
    if (Number(weightedAveragePercent) && Number(weightedAveragePercent) !== Number(expectedTotals[1])) {
      onTotalPercentCalculate(value);
    }
  };

  useDebouncedSave({
    data: totalRow,
    saveFunction: handleDebouncedSave,
    debounceTime: 500,
    skipExecutionOnFirstRender: true,
  });

  useDebouncedSave({
    data: weightedAveragePercent,
    saveFunction: handleWeightedAveragePercentSave,
    debounceTime: 500,
    skipExecutionOnFirstRender: true,
  });

  return (
    <Grid container display="flex" alignItems="center" gap={1}>
      <Grid item className={styles.spacingContainer}></Grid>
      <Grid item sx={{ width: firstColumnWidth }} className={styles.summaryTitle}>
        {t('total')}
      </Grid>
      <Grid item xs>
        <TotalSumInput
          name={EXPENSE_COLUMN_NAMES.MONTHLY_EXPENSES}
          value={totalRow[`${FORM_TABLE_COLUMN_NAME}0`]}
          look={getClassForColumn(0)}
          disabled={disabled}
          className={styles.totalSumInput}
        />
      </Grid>
      <Grid item xs>
        <TotalSumInput
          name={EXPENSE_COLUMN_NAMES.ANNUAL_GROWTH}
          value={weightedAveragePercent || expectedTotals[1]}
          look={LOOK.SUCCESS}
          disabled={disabled}
          className={styles.totalSumInput}
        />
      </Grid>
    </Grid>
  );
};
